import React from 'react'
import { useHistory, Link, useLocation } from 'react-router-dom'
import { authService } from '@oneblink/apps-react'
import { useAuth } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'
import styled from 'styled-components'

import useFetchForm from '../../hooks/useFetchForm'
import useQuery from '../../hooks/useQuery'
import useSanitisedParams from '../../hooks/useSanitisedParams'
import useGetClarification from '../../hooks/useGetClarification'

import { useHeaderButtonsContext } from '../../providers/HeaderButtonsProvider'
import { useAppConfig } from '../../providers/AppConfigProvider'
import { useHelpMessage } from '../../providers/HelpMessageProvider'
import { useSystemMaintenance } from '../../providers/SystemMaintenanceProvider'

import { getDraftData, getDraftMeta } from '../../lib/drafts'
import { getHelpMessage } from '../../lib/infoElementHelpers'
import { findTag } from '../../lib/definition-manipulation'

import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import Dialog, {
  DialogHeading,
  DialogHeadingText,
} from '../../components/Dialog'
import { P } from '../../components/TextComponents'
import {
  FullPageCenteredPageContent,
  PageContent,
} from '../../components/LayoutComponents'
import { ButtonPrimary } from '../../components/Page'

import DefaultForm from './Form'
import ComplaintsForm from './ControlledForms/ComplaintsForm'
import ResourceOrderForm from './ControlledForms/ResourceOrderForm'
import LGAResourceOrderForm from './ControlledForms/LGAResourceOrderForm'
import FoodLicenceApplicationFormContainer from './ControlledForms/FoodLicenceApplicationForm'
import FoodNotificationFormContainer from './ControlledForms/FoodNotificationForm'
import BeekeeperRegistrationForm from './ControlledForms/BeekeeperRegistrationForm'
import VehicleVesselVariationForm from './ControlledForms/VehicleVesselVariationForm'
import BeekeeperForm from './UncontrolledForms/BeekeeperForm'
import ActivityReportForm from './UncontrolledForms/ActivityReport'
import AuthenticatedForm from './AuthenticatedForm'
import GenericControlledForm from './ControlledForms/Generic'

import config from '../../config'
import {
  useToFormId,
  useToFormSlug,
} from '../../providers/FormDefinitionsProvider'

const FormWrapper = styled.div`
  width: 100%;
`
FormWrapper.displayName = 'FormWrapper'

const ButtonContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`
ButtonContainer.displayName = 'ButtonContainer'

function Form({
  formDefinition,
  preFillData,
}: {
  formDefinition: FormTypes.Form
  preFillData: Record<string, unknown>
}) {
  switch (formDefinition.id) {
    case config.FORMS.COMPLAINTS_FORM_ID:
      return (
        <ComplaintsForm
          form={formDefinition}
          preFillData={preFillData}
        ></ComplaintsForm>
      )
    case config.FORMS.RESOURCE_ORDER_FORM_ID:
      return (
        <ResourceOrderForm form={formDefinition} preFillData={preFillData} />
      )
    case config.FORMS.LGA_RESOURCE_ORDER_FORM_ID:
      return (
        <LGAResourceOrderForm form={formDefinition} preFillData={preFillData} />
      )
    case config.FORMS.FOOD_LICENCE_APPLICATION_FORM_ID:
    case config.FORMS.AI_FOOD_LICENCE_APPLICATION_FORM_ID:
      return (
        <FoodLicenceApplicationFormContainer
          form={formDefinition}
          preFillData={preFillData}
        />
      )

    case config.FORMS.FOOD_NOTIFICATION_FORM_ID:
      return (
        <FoodNotificationFormContainer
          form={formDefinition}
          preFillData={preFillData}
        />
      )
    case config.FORMS.BEEKEEPER_REGISTRATION_FORM_ID:
      return (
        <BeekeeperRegistrationForm
          form={formDefinition}
          preFillData={preFillData}
        />
      )
    case config.FORMS.VEHICLE_VESSEL_VARIATION_FORM_ID:
      return (
        <VehicleVesselVariationForm
          form={formDefinition}
          preFillData={preFillData}
        />
      )
    case config.FORMS.BEEKEEPER_FORM_ID:
      return <BeekeeperForm form={formDefinition} preFillData={preFillData} />
    case config.FORMS.ACTIVITY_REPORT_FORM_ID:
      return (
        <ActivityReportForm form={formDefinition} preFillData={preFillData} />
      )
    default:
      const usesCustomFunctions = findTag(formDefinition, 'customFunctions')
      if (usesCustomFunctions) {
        return (
          <GenericControlledForm
            form={formDefinition}
            preFillData={preFillData}
          />
        )
      }

      return <DefaultForm form={formDefinition} preFillData={preFillData} />
  }
}

export default function FormContainer() {
  const { formId: formSlugOrId } = useSanitisedParams<{ formId: string }>()
  const history = useHistory()

  const query = useQuery()
  const location = useLocation()

  const { showDraftButton, hideDraftButton } = useHeaderButtonsContext()

  const [isFetchingDraftData, setIsFetchingDraftData] =
    React.useState<boolean>(true)
  const [fetchDraftError, setFetchDraftError] = React.useState<Error | null>(
    null,
  )

  const [
    {
      fetchErrorMessage: fetchClarificationErrorMessage,
      fetchErrorCode: fetchClarificationErrorCode,
      isFetching: isFetchingClarification,
    },
    {
      setState: setFetchClarifcationState,
      fetchClarification,
      setFetchErrorMessage,
    },
  ] = useGetClarification()

  const [preFillData, setPreFillData] =
    React.useState<Record<string, unknown>>()

  const [canResumeDraft, setCanResumeDraft] = React.useState<boolean>(true)
  const formId = useToFormId(formSlugOrId)
  const formSlug = useToFormSlug(formSlugOrId)

  const {
    formDefinition,
    fetchError,
    isFetchingForm,
    personalisationSubmission,
    personalisationFetchingError,
  } = useFetchForm(formId, formSlug, true)

  const { formsOAuthClientId } = useAppConfig()

  const { setHelpMessage } = useHelpMessage()

  const startFormAgain = React.useCallback(() => {
    //remove all query params
    history.push(window.location.pathname)
    setFetchClarifcationState({ isFetching: false })
    setPreFillData({})
    setFetchErrorMessage(undefined)
  }, [history, setFetchErrorMessage, setFetchClarifcationState])

  const { updateMaintenanceWindow, isInMaintenanceWindow } =
    useSystemMaintenance()

  const isPublished = React.useMemo(() => {
    const now = new Date()
    if (formDefinition && formDefinition.publishStartDate) {
      if (now < new Date(formDefinition.publishStartDate)) {
        return false
      }
    }
    if (formDefinition && formDefinition.publishEndDate) {
      if (now > new Date(formDefinition.publishEndDate)) {
        return false
      }
    }
    return true
  }, [formDefinition])

  React.useEffect(() => {
    updateMaintenanceWindow && updateMaintenanceWindow()
  }, [updateMaintenanceWindow])

  //classes will be used for preventing submit
  React.useEffect(() => {
    const setMaintenanceClass = () => {
      console.log('checking if in maintenance window')
      if (isInMaintenanceWindow()) {
        window.document.body.classList.add('maintenance-mode')
      } else {
        window.document.body.classList.remove('maintenance-mode')
      }
    }

    const intervalId = setInterval(setMaintenanceClass, 5000)

    return () => {
      clearInterval(intervalId)
      setMaintenanceClass()
    }
  }, [isInMaintenanceWindow])

  React.useEffect(() => {
    document.title = formDefinition?.name || config.CUSTOMER_NAME
  }, [formDefinition?.name])

  const signIn = React.useCallback(async () => {
    try {
      if (formsOAuthClientId) {
        await authService.loginHostedUI(formsOAuthClientId)
      }
    } catch (e) {
      if (e instanceof Error) {
        console.log(e.message)
      }
    }
  }, [formsOAuthClientId])

  const queryStringPreFillData = React.useMemo(() => {
    const query = new URLSearchParams(location.search)
    const preFillData = query.get('preFillData')
    return typeof preFillData === 'string' ? JSON.parse(preFillData) : {}
  }, [location.search])

  React.useEffect(() => {
    const getDraft = async (
      draftDataUrl: typeof query.draftDataUrl,
      trackingCode: string,
    ): Promise<Record<string, unknown> | undefined> => {
      if (typeof draftDataUrl !== 'string') {
        setIsFetchingDraftData(false)
        return
      }
      const doc = new DOMParser().parseFromString(draftDataUrl, 'text/html')
      const decodedDraftUrl = doc.documentElement.textContent as string
      try {
        const { username } = await getDraftMeta(trackingCode)

        if (formDefinition?.isAuthenticated) {
          if (authService.isLoggedIn()) {
            const profile = authService.getUserProfile()
            if (profile?.username === username) {
              return await getDraftData(decodedDraftUrl)
            } else {
              setCanResumeDraft(false)
            }
          } else {
            signIn()
          }
        } else {
          return await getDraftData(decodedDraftUrl)
        }
      } catch (e) {
        console.error(e)
        if (e instanceof Error) {
          setFetchDraftError(e)
        }
      }
    }

    const getClarification = async () => {
      if (!query.previousFormSubmissionApprovalId || !query.trackingCode) {
        return
      }
      return await fetchClarification({
        previousFormSubmissionApprovalId:
          query.previousFormSubmissionApprovalId as string,
        externalId: query.trackingCode as string,
      })
    }

    if (!formDefinition) {
      return
    }

    Promise.all([
      getDraft(query?.draftDataUrl, query.trackingCode as string).finally(
        () => {
          setIsFetchingDraftData(false)
          // Only strip draftDataUrl when present — do not wipe other query
          // params (e.g. formSubmissionPaymentId for the payment modal).
          if (typeof query?.draftDataUrl === 'string') {
            let sanitisedUrlPath = `/forms/${formSlugOrId}`
            if (query.trackingCode) {
              sanitisedUrlPath += `?trackingCode=${query.trackingCode}`
            }

            history.replace(sanitisedUrlPath)
          }
        },
      ),
      getClarification(),
    ]).then(([draftData, approvalData]) => {
      if (query.previousFormSubmissionApprovalId) {
        setPreFillData({
          ...approvalData,
        })
      } else {
        setPreFillData({
          ...queryStringPreFillData,
          ...draftData,
          ...personalisationSubmission,
        })
      }
    })
  }, [
    query,
    formSlugOrId,
    history,
    formDefinition,
    signIn,
    queryStringPreFillData,
    personalisationSubmission,
    fetchClarification,
  ])

  React.useEffect(() => {
    if (formDefinition) {
      const helpMessage = getHelpMessage(formDefinition.elements)
      if (helpMessage) {
        setHelpMessage(helpMessage)
      }
    }

    return () => {
      setHelpMessage('')
    }
  }, [formDefinition, setHelpMessage])

  React.useEffect(() => {
    showDraftButton && showDraftButton()

    return hideDraftButton
  }, [hideDraftButton, showDraftButton])

  const sanitisedFetchClarificationErrorMessage = React.useMemo(() => {
    const errorMap: Record<number, string> = {
      404: 'Your previous submission could not be found',
      403: fetchClarificationErrorMessage?.message || 'Access denied',
    }

    if (fetchClarificationErrorCode) {
      const message = errorMap[fetchClarificationErrorCode]
      if (message) return message
    }

    if (fetchClarificationErrorMessage) {
      return 'There was problem opening this form'
    }
  }, [fetchClarificationErrorCode, fetchClarificationErrorMessage])

  const { isLoggedIn } = useAuth()

  if (fetchError || fetchDraftError || fetchClarificationErrorMessage) {
    if (
      fetchError?.httpStatusCode === 401 ||
      fetchClarificationErrorCode === 401
    ) {
      signIn()
      return <LoadingSpinner>Automatically signing in</LoadingSpinner>
    }
    if (fetchError || fetchDraftError) {
      return (
        <ErrorMessage>
          An error has occurred while attempting to load this form
        </ErrorMessage>
      )
    }
  }

  if (
    isFetchingForm ||
    isFetchingDraftData ||
    isFetchingClarification ||
    !preFillData
  ) {
    return <LoadingSpinner>Fetching data</LoadingSpinner>
  }

  if (!formDefinition) {
    return <ErrorMessage>Unable to find form</ErrorMessage>
  }

  if (!canResumeDraft) {
    return (
      <ErrorMessage>
        You are not authorised to access this form. Please check your details
        and try again.
      </ErrorMessage>
    )
  }

  if (sanitisedFetchClarificationErrorMessage) {
    return (
      <ErrorMessage>
        <div>{sanitisedFetchClarificationErrorMessage}</div>
        <ButtonContainer>
          <button
            className="button ob-button is-primary"
            onClick={startFormAgain}
          >
            Start again
          </button>
        </ButtonContainer>
      </ErrorMessage>
    )
  }

  //do this so that the Form doesn't ask us if we want to discard
  if (query.draftDataUrl) {
    return <LoadingSpinner></LoadingSpinner>
  }

  if (!isPublished) {
    return (
      <PageContent>
        <FullPageCenteredPageContent>
          <div
            dangerouslySetInnerHTML={{
              __html:
                formDefinition.unpublishedUserMessage ||
                'The system is currently unavailable, please try again later',
            }}
          />
        </FullPageCenteredPageContent>
      </PageContent>
    )
  }

  if (personalisationFetchingError) {
    return (
      <>
        <Dialog isOpen={!!personalisationFetchingError}>
          <div style={{ minHeight: '10rem' }}>
            <DialogHeading>
              <DialogHeadingText>Error loading form</DialogHeadingText>
            </DialogHeading>
            <P>{personalisationFetchingError?.message}</P>
            {isLoggedIn && (
              <Link to="/me">
                <ButtonPrimary>Profile</ButtonPrimary>
              </Link>
            )}
          </div>
        </Dialog>
      </>
    )
  }

  return (
    <FormWrapper
      className={`${[
        formDefinition.description,
        ...(formDefinition.customCssClasses || []),
      ].join(' ')}`}
    >
      <AuthenticatedForm formHasAuthentication={formDefinition.isAuthenticated}>
        <Form formDefinition={formDefinition} preFillData={preFillData} />
      </AuthenticatedForm>
    </FormWrapper>
  )
}
